import { useDisclosure } from "@chakra-ui/hooks";
import { Modal, ModalOverlay } from "@chakra-ui/modal";
import React, { useEffect, useRef } from "react";
import { MultiSelect } from "react-multi-select-component";
import InputField from "components/fields/InputField";
import { MdHighlightOff } from "react-icons/md";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const GLOBAL_ID = '__dr_global_styles__';
function injectGlobalStyles() {
  if (typeof document === 'undefined' || document.getElementById(GLOBAL_ID)) return;
  const s = document.createElement('style');
  s.id = GLOBAL_ID;
  s.textContent = `
    /* ── DateRange global overrides ── */
    .react-datepicker-popper { z-index: 99999 !important; }
    .react-datepicker {
      font-family: 'Plus Jakarta Sans', sans-serif !important;
      border: 1.5px solid #e2e8f0 !important;
      border-radius: 14px !important;
      box-shadow: 0 8px 32px rgba(15,23,42,0.14) !important;
      overflow: hidden;
    }
    .react-datepicker__header {
      background: #f8fafc !important;
      border-bottom: 1px solid #e2e8f0 !important;
      border-radius: 0 !important;
      padding-top: 10px !important;
    }
    .react-datepicker__current-month {
      font-size: 13px !important; font-weight: 700 !important; color: #0f172a !important;
    }
    .react-datepicker__day-name {
      font-size: 11px !important; font-weight: 700 !important; color: #94a3b8 !important;
    }
    .react-datepicker__day {
      font-size: 12.5px !important; font-weight: 500 !important; color: #374151; border-radius: 7px !important;
    }
    .react-datepicker__day:hover { background: #eff6ff !important; color: #1d4ed8 !important; }
    .react-datepicker__day--selected,
    .react-datepicker__day--in-range,
    .react-datepicker__day--range-start,
    .react-datepicker__day--range-end { background: #2563eb !important; color: #fff !important; }
    .react-datepicker__day--in-selecting-range { background: #bfdbfe !important; color: #1d4ed8 !important; }
    .react-datepicker__day--keyboard-selected { background: #eff6ff !important; color: #1d4ed8 !important; }
    .react-datepicker__navigation-icon::before { border-color: #64748b !important; }
    .react-datepicker__close-icon::after {
      background-color: #94a3b8 !important; font-size: 13px !important;
      width: 16px !important; height: 16px !important;
    }
    .react-datepicker__close-icon:hover::after { background-color: #dc2626 !important; }

    /* ── Mobile calendar: constrain to viewport ── */
    @media (max-width: 600px) {
      .react-datepicker-popper {
        /* Prevent it going off right edge */
        max-width: calc(100vw - 24px) !important;
        left: 12px !important;
        right: auto !important;
        /* Override any inline transform-based positioning */
        transform: translate3d(0px, 42px, 0px) !important;
      }
      .react-datepicker {
        width: calc(100vw - 24px) !important;
        border-radius: 12px !important;
      }
      .react-datepicker__month-container {
        width: 100% !important;
        float: none !important;
      }
      /* Equally distribute day cells */
      .react-datepicker__week,
      .react-datepicker__day-names {
        display: flex !important;
        justify-content: space-between !important;
        padding: 0 6px !important;
      }
      .react-datepicker__day-name,
      .react-datepicker__day {
        flex: 1 !important;
        width: auto !important;
        margin: 1px 0 !important;
        font-size: 12px !important;
      }
      .react-datepicker__current-month { font-size: 12.5px !important; }
    }

    @media (max-width: 380px) {
      .react-datepicker-popper {
        max-width: calc(100vw - 16px) !important;
        left: 8px !important;
      }
      .react-datepicker { width: calc(100vw - 16px) !important; }
      .react-datepicker__day { font-size: 11px !important; }
      .react-datepicker__day-name { font-size: 10px !important; }
    }
  `;
  document.head.appendChild(s);
}

interface Option {
  label: string;
  value: string;
}

const STATUSES: Option[] = [
  {
    label: "Active",
    value: "Active",
  },
  {
    label: "Paused",
    value: "Pause",
  },
];

const PAYMENT_STATUSES: Option[] = [
  {
    label: "Daily",
    value: "daily",
  },
  {
    label: "Weekly",
    value: "weekly",
  },
  {
    label: "Monthly",
    value: "monthly",
  },
];

const ScheduleFilterModal = ({
  subClientsData, filter, setFilter, fetchAllsettlement,
  setClearClick,allOrgDetails,filterData,setPageSize
}: {
  subClientsData?: any; setPage?: any; filter?: any;
  setClearClick?: () => void; setFilter?: (e: any) => void;
  roleData?: any; fetchAllsettlement: any;allOrgDetails?:any;filterData: () => void;setPageSize:any
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isCloseModal, setIsCloseModal] = React.useState(false);
  const [isApplied, setIsApplied] = React.useState(false);
  const [isDirty, setIsDirty] = React.useState(false);
  const [localFilter, setLocalFilter] = React.useState({
    clients: [] , organizations: [] , settlement_type: [] , settlement_status : [],dates: null
  });

  const [selectedSettlementStatus, setSelectedSettlementStatus] = React.useState<Option[]>([]);
  const [selectedPaymentStatus, setSelectedPaymentStatus] = React.useState<Option[]>([]);

  const [isMobile, setIsMobile] = React.useState(false);

  const originalFilterRef = React.useRef({
      filter,
      selectedSettlementStatus,
      selectedPaymentStatus
    });
  const handleClose = () => {
    if (isDirty && !isApplied) {
        const snap = originalFilterRef.current;
        setFilter(snap.filter);
        setLocalFilter(snap.filter);
        setSelectedSettlementStatus(snap.selectedSettlementStatus);
        setSelectedPaymentStatus(snap.selectedPaymentStatus);
    }
    setIsDirty(false);
    setIsCloseModal(true);
    setTimeout(() => { setIsCloseModal(false); onClose(); }, 300);
  };

  useEffect(() => {
      injectGlobalStyles();
      const check = () => setIsMobile(window.innerWidth <= 600);
      check();
      window.addEventListener('resize', check);
      return () => window.removeEventListener('resize', check);
    }, []);
    

    const hasActiveFilter =
      filter?.clients?.length > 0 || filter?.organizations.length > 0 ||
      filter?.settlement_type?.length > 0 || filter?.settlement_status?.length > 0 || !!filter?.dates;
  
    const activeCount = [
      filter?.clients?.length > 0,
      filter?.organizations?.length > 0, 
      filter?.settlement_type?.length > 0, 
      filter?.settlement_status?.length > 0,
      filter?.dates && Object.keys(filter.dates).length > 0,
    ].filter(Boolean).length;

    return (
        <>
            <button
                className="af-trigger-btn"
                onClick={() => { 
                    originalFilterRef.current = {
                        filter: { ...filter },
                        selectedSettlementStatus: [...selectedSettlementStatus],
                        selectedPaymentStatus: [...selectedPaymentStatus],
                    }; 
                    setIsApplied(false); 
                    onOpen(); }}
            >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
                </svg>
                {hasActiveFilter ? "Update Filter" : "Apply Filter"}
                {hasActiveFilter && <span className="af-trigger-badge">{activeCount}</span>}
            </button>

            {hasActiveFilter && (
                <button
                className="af-clear-btn"
                onClick={() => {
                    setFilter({ clients: [] , organizations: [] , settlement_type: [] , settlement_status : [],dates: null });
                    setLocalFilter({ clients: [] , organizations: [] , settlement_type: [] , settlement_status : [],dates: null });
                    setIsApplied(false);
                    setSelectedSettlementStatus([])
                    setSelectedPaymentStatus([])
                    setClearClick?.();
                    setPageSize(50)
                }}
                >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
                Clear Filter
                </button>
            )}

            <Modal isOpen={isOpen} onClose={() => {}}>
                <ModalOverlay bg="rgba(15,23,42,0.55)" backdropFilter="blur(4px)" />
                <div className={`afm-panel${isOpen && !isCloseModal ? " afm-panel--open" : " afm-panel--close"}`}>
                    
                    <div className="afm-top-glow" />

                    <div className="afm-header">
                        <div className="afm-header-brand">
                            <div className="afm-header-logo">
                            <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
                            </svg>
                            </div>
                            <div>
                            <h2 className="afm-header-title">Filter Schedule Settlements</h2>
                            <p className="afm-header-sub">Narrow results by organization, clients, status and more</p>
                            </div>
                        </div>
                        <button className="afm-close-btn" onClick={handleClose} aria-label="Close">
                            <MdHighlightOff size={22} />
                        </button>
                    </div>

                    <div className="afm-body">

                        <div className="afm-section">
                            <div className="afm-section-icon afm-section-icon--blue">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                                <circle cx="9" cy="7" r="4"/>
                                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                            </svg>
                            </div>
                            <div className="afm-section-content">
                            <label className="afm-label">Organizations</label>
                            <p className="afm-hint">Filter by one or more organizations</p>
                            <MultiSelect 
                                options={allOrgDetails?.map((m: any) => ({ label: m?.label, value: m?.value })) ?? []}
                                value={allOrgDetails?.map((m: any) => ({ label: m?.label, value: m?.value })).filter((f: any) => filter?.organizations?.includes(f?.value)) ?? []}
                                onChange={(e: any) => {
                                    setIsDirty(true);
                                    setFilter?.((p: any) => ({ ...p, organizations: e.map((m: any) => m?.value) }));
                                    setLocalFilter?.((p: any) => ({ ...p, organizations: e.map((m: any) => m?.value) }));
                                }}
                                labelledBy="Select organizations" isCreatable={true}
                                />
                            </div>
                        </div>

                        <div className="afm-divider" />

                        <div className="afm-section">
                            <div className="afm-section-icon afm-section-icon--blue">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                                <circle cx="9" cy="7" r="4"/>
                                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                            </svg>
                            </div>
                            <div className="afm-section-content">
                            <label className="afm-label">Clients</label>
                            <p className="afm-hint">Filter by one or more clients</p>
                            <MultiSelect 
                                options={subClientsData?.map((m: any) => ({ label: m?.name, value: m?.client_id })) ?? []}
                                value={subClientsData?.map((m: any) => ({ label: m?.name, value: m?.client_id })).filter((f: any) => filter?.clients?.includes(f?.value)) ?? []}
                                onChange={(e: any) => {
                                    setIsDirty(true);
                                    setFilter?.((p: any) => ({ ...p, clients: e.map((m: any) => m?.value) }));
                                    setLocalFilter?.((p: any) => ({ ...p, clients: e.map((m: any) => m?.value) }));
                                }}
                                labelledBy="Select clients" isCreatable={true}
                                />
                            </div>
                        </div>

                        <div className="afm-divider" />

                        <div className="afm-section">
                            <div className="afm-section-icon afm-section-icon--purple">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                                    <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
                                    <line x1="3" y1="10" x2="21" y2="10"/>
                                </svg>
                            </div>
                            <div className="afm-section-content">
                                <label className="afm-label"> Date Range </label>
                                <p className="afm-hint">Filter records within a specific date range</p>
                                <div className="dr-wrap">
                                    <DatePicker
                                    onKeyDown={(e) => e.preventDefault()}
                                    withPortal={isMobile}
                                    portalId={isMobile ? "dr-portal" : undefined}
                                    popperPlacement="bottom-start"
                                    className="dr-input"
                                    placeholderText="Select date range…"
                                    startDate={filter?.start_date ? new Date(filter?.start_date) : null}
                                    endDate={filter?.end_date ? new Date(filter?.end_date) : null}
                                    selectsRange={true}
                                    isClearable={true}
                                    maxDate={new Date()}
                                    onChange={(dateRange) => {
                                        setIsDirty(true);
                                        const [a, b] = dateRange as [Date | null, Date | null];
                                        setFilter((prev: any) => ({
                                        ...prev,
                                        start_date: a,
                                        end_date: b,
                                        dates: a != null && b != null ? [a, b] : null,
                                        }));
                                    }}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="afm-divider" />

                        <div className="afm-section">
                            <div className="afm-section-icon afm-section-icon--green">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12"/>
                            </svg>
                            </div>
                            <div className="afm-section-content">
                                <label className="afm-label">Settlement Status</label>
                                <p className="afm-hint">Filter by one or more settlement statuses</p>
                                <MultiSelect
                                options={STATUSES} value={selectedSettlementStatus}
                                onChange={(e: any) => { setIsDirty(true); setSelectedSettlementStatus(e); setFilter?.({ ...filter, settlement_status: e.map((i: Option) => i.value) }); }}
                                labelledBy="Select settlement status" isCreatable={true}
                                />
                            </div>
                        </div>

                        <div className="afm-divider" />

                        <div className="afm-section">
                            <div className="afm-section-icon afm-section-icon--green">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12"/>
                            </svg>
                            </div>
                            <div className="afm-section-content">
                                <label className="afm-label">Schedule Type</label>
                                <p className="afm-hint">Filter by one or more schedule type</p>
                                <MultiSelect
                                options={PAYMENT_STATUSES} value={selectedPaymentStatus}
                                onChange={(e: any) => { setIsDirty(true); setSelectedPaymentStatus(e); setFilter?.({ ...filter, settlement_type: e.map((i: Option) => i.value) }); }}
                                labelledBy="Select schedule type" isCreatable={true}
                                />
                            </div>
                        </div>

                        {hasActiveFilter && (
                            <div className="afm-summary">
                                <p className="afm-summary-label">Active filters:</p>
                                <div className="afm-summary-chips">
                                {filter?.clients?.length > 0 && <span className="afm-chip afm-chip--blue">Clients ({filter.clients.length})</span>}
                                {filter?.organizations?.length > 0 && <span className="afm-chip afm-chip--blue">Organizations ({filter.organizations.length})</span>}
                                {filter?.settlement_type?.length > 0 && <span className="afm-chip afm-chip--green">Schedule Type  ({filter.settlement_type.length})</span>}
                                {filter?.settlement_status?.length > 0 && <span className="afm-chip afm-chip--green">Settlement Status  ({filter.settlement_status.length})</span>}
                                {filter?.dates && (
                                    <span className="afm-chip afm-chip--purple">
                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                                        <line x1="3" y1="10" x2="21" y2="10"/>
                                    </svg>
                                    Date range set
                                    </span>
                                )}
                                </div>
                            </div>
                        )}

                    </div>

                    <div className="afm-footer">
                        <div className="afm-footer-note">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10"/>
                            <line x1="12" y1="8" x2="12" y2="12"/>
                            <line x1="12" y1="16" x2="12.01" y2="16"/>
                        </svg>
                        Click Apply to update results
                        </div>
                        <div className="afm-footer-btns">
                        <button className="afm-btn-cancel" onClick={handleClose}>Cancel</button>
                        <button
                            className="afm-btn-apply"
                            onClick={() => {
                                filterData();
                                originalFilterRef.current = {
                                    filter,
                                    selectedSettlementStatus,
                                    selectedPaymentStatus
                                };
                                setIsApplied(true);
                                setIsDirty(false);
                                onClose();
                                setPageSize(50)
                            }}
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12"/>
                            </svg>
                            Apply Filters
                        </button>
                        </div>
                    </div>

                </div>
            </Modal>
            <style>{`
            @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

            .af-trigger-btn { position:relative; display:inline-flex; align-items:center; gap:7px; font-family:'Plus Jakarta Sans',sans-serif; font-size:13px; font-weight:700; color:#fff; padding:9px 18px; border-radius:10px; border:none; background:linear-gradient(135deg,#1d4ed8,#2563eb); cursor:pointer; box-shadow:0 3px 12px rgba(37,99,235,.28); transition:transform .15s, box-shadow .18s; }
            .af-trigger-btn:hover { transform:translateY(-1px); box-shadow:0 6px 20px rgba(37,99,235,.36); }
            .af-trigger-badge { display:inline-flex; align-items:center; justify-content:center; min-width:18px; height:18px; padding:0 5px; border-radius:9px; background:#fbbf24; color:#1e3a8a; font-size:10px; font-weight:800; border:1.5px solid rgba(255,255,255,.7); }
            .af-clear-btn { display:inline-flex; align-items:center; gap:5px; font-family:'Plus Jakarta Sans',sans-serif; font-size:13px; font-weight:600; color:#dc2626; padding:9px 16px; border-radius:10px; background:#fff7f7; border:1.5px solid #fecaca; cursor:pointer; transition:all .15s; }
            .af-clear-btn:hover { background:#fee2e2; border-color:#fca5a5; }

            .afm-panel { font-family:'Plus Jakarta Sans',sans-serif; position:fixed; top:0; right:0; bottom:0; width:100%; max-width:520px; background:#fff; z-index:1004; display:flex; flex-direction:column; box-shadow:-12px 0 60px rgba(15,23,42,.18); transition:transform .35s cubic-bezier(0.16,1,0.3,1); overflow:hidden; }
            .afm-panel--open  { transform:translateX(0); }
            .afm-panel--close { transform:translateX(100%); }

            .afm-top-glow { height:3px; background:linear-gradient(90deg,#0f2744,#2563eb,#7c3aed,#2563eb,#0f2744); background-size:200% 100%; animation:afmGlow 3s linear infinite; flex-shrink:0; }
            @keyframes afmGlow { 0%{background-position:0%} 100%{background-position:200%} }

            .afm-header { padding:15px 26px; background:linear-gradient(135deg,#0f2744 0%,#1d4ed8 60%,#2563eb 100%); display:flex; align-items:center; justify-content:space-between; flex-shrink:0; }
            .afm-header-brand { display:flex; align-items:center; gap:13px; }
            .afm-header-logo { width:42px; height:42px; border-radius:12px; background:rgba(255,255,255,.15); border:1.5px solid rgba(255,255,255,.25); display:flex; align-items:center; justify-content:center; flex-shrink:0; }
            .afm-header-title { font-size:16px; font-weight:800; color:#fff; letter-spacing:-.3px; margin-bottom:2px; line-height:1; }
            .afm-header-sub { font-size:12px; color:rgba(255,255,255,.55); font-weight:500; }
            .afm-close-btn { background:none; border:none; cursor:pointer; color:rgba(255,255,255,.65); display:flex; align-items:center; padding:2px; transition:color .15s; }
            .afm-close-btn:hover { color:#fff; }

            .afm-body { flex:1; padding:20px 26px; overflow-y:auto; display:flex; flex-direction:column; gap:4px; }
            .afm-body::-webkit-scrollbar { width:4px; }
            .afm-body::-webkit-scrollbar-thumb { background:#cbd5e1; border-radius:4px; }

            .afm-section { display:flex; gap:16px; align-items:flex-start; padding:16px 0; }
            .afm-section-icon { width:44px; height:44px; border-radius:12px; flex-shrink:0; display:flex; align-items:center; justify-content:center; margin-top:2px; }
            .afm-section-icon--blue   { background:linear-gradient(135deg,#eff6ff,#dbeafe); border:1.5px solid #bfdbfe; color:#2563eb; }
            .afm-section-icon--purple { background:linear-gradient(135deg,#faf5ff,#ede9fe); border:1.5px solid #ddd6fe; color:#7c3aed; }
            .afm-section-icon--green  { background:linear-gradient(135deg,#f0fdf4,#dcfce7); border:1.5px solid #bbf7d0; color:#16a34a; }
            .afm-section-icon--amber  { background:linear-gradient(135deg,#fffbeb,#fef3c7); border:1.5px solid #fde68a; color:#d97706; }
            .afm-section-content { flex:1; display:flex; flex-direction:column; gap:6px; }
            .afm-label { font-size:13px; font-weight:700; color:#0f172a; }
            .afm-hint { font-size:12px; color:#94a3b8; font-weight:500; margin-bottom:2px; }

            .dr-wrap {
            position: relative; display: flex; align-items: center; width: 100%;
            }
            .dr-icon {
            position: absolute; left: 12px; color: #94a3b8;
            display: flex; align-items: center; pointer-events: none; z-index: 1;
            }
            .dr-input {
            width: 100%;
            height: 42px;
            padding: 0 36px 0 10px;
            background: #f8fafc;
            border: 1.5px solid #e2e8f0;
            border-radius: 10px;
            font-family: 'Plus Jakarta Sans', sans-serif;
            font-size: 13px; font-weight: 500; color: #0f172a;
            outline: none;
            transition: border-color 0.18s, box-shadow 0.18s, background 0.18s;
            cursor: pointer; box-sizing: border-box; -webkit-appearance: none;
            white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
            }
            .dr-input::placeholder { color: #cbd5e1; }
            .dr-input:hover:not(:disabled) { border-color: #cbd5e1; background: #f1f5f9; }
            .dr-input:focus { border-color: #2563eb; background: #fff; box-shadow: 0 0 0 3px rgba(37,99,235,0.1); }
            .dr-input:disabled { opacity: 0.55; cursor: not-allowed; background: #f1f5f9; }

            /* Portal overlay on mobile */
            .react-datepicker__portal {
            background: rgba(15,23,42,0.45) !important;
            backdrop-filter: blur(3px) !important;
            z-index: 99999 !important;
            }
            .react-datepicker__portal .react-datepicker {
            border-radius: 18px !important;
            box-shadow: 0 24px 60px rgba(15,23,42,0.2) !important;
            overflow: hidden;
            }
            /* Make portal calendar scrollable on very short screens */
            .react-datepicker__portal .react-datepicker__month-container {
            max-height: 80vh; overflow-y: auto;
            }

            @media (max-width: 480px) {
            .dr-input { font-size: 12px; height: 40px; padding: 0 32px 0 10px;width: 266px !important; }
            .dr-icon svg { width: 13px; height: 13px; }
            }
            @media (max-width: 380px) {
            .dr-input { font-size: 11.5px; height: 38px;width: 266px !important; }
            }
            /* Date picker */
            .fm-datepicker-wrap .react-datepicker-wrapper { width:100%; }
            .fm-datepicker {
            width:100%; padding:11px 14px;
            background:#f8fafc; border:1.5px solid #e2e8f0; border-radius:11px;
            font-family:'Plus Jakarta Sans',sans-serif; font-size:13.5px; color:#0f172a;
            outline:none; transition:border-color .18s; box-sizing:border-box;
            }
            .fm-datepicker:focus { border-color:#0284c7; background:#fff; box-shadow:0 0 0 3px rgba(2,132,199,.1); }

            .afm-divider {
                height: 1px;
                background: #f1f5f9;
                margin: 4px 0;
                flex-shrink: 0;
            }

            .afm-summary { margin-top:8px; padding:14px 16px; background:linear-gradient(135deg,#eff6ff,#dbeafe); border:1.5px solid #bfdbfe; border-radius:12px; }
            .afm-summary-label { font-size:11px; font-weight:700; color:#1d4ed8; text-transform:uppercase; letter-spacing:0.5px; margin-bottom:9px; }
            .afm-summary-chips { display:flex; flex-wrap:wrap; gap:6px; }
            .afm-chip { display:inline-flex; align-items:center; gap:5px; font-size:11.5px; font-weight:600; padding:4px 10px; border-radius:7px; }
            .afm-chip--blue   { background:#eff6ff; border:1.5px solid #bfdbfe; color:#1d4ed8; }
            .afm-chip--purple { background:#ede9fe; border:1.5px solid #c4b5fd; color:#6d28d9; }
            .afm-chip--green  { background:#f0fdf4; border:1.5px solid #bbf7d0; color:#15803d; }
            .afm-chip--amber  { background:#fffbeb; border:1.5px solid #fde68a; color:#b45309; }

            .afm-footer { padding:14px 26px; border-top:1.5px solid #f1f5f9; background:#fafbfc; display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:10px; flex-shrink:0; }
            .afm-footer-note { display:flex; align-items:center; gap:5px; font-size:12px; color:#94a3b8; font-weight:500; }
            .afm-footer-btns { display:flex; align-items:center; gap:8px; }
            .afm-btn-cancel { font-family:'Plus Jakarta Sans',sans-serif; font-size:13px; font-weight:600; color:#64748b; padding:9px 16px; border-radius:10px; border:1.5px solid #e2e8f0; background:#fff; cursor:pointer; transition:all .15s; }
            .afm-btn-cancel:hover { background:#f1f5f9; color:#374151; }
            .afm-btn-apply { display:inline-flex; align-items:center; gap:7px; font-family:'Plus Jakarta Sans',sans-serif; font-size:13.5px; font-weight:700; color:#fff; padding:10px 22px; border-radius:10px; border:none; background:linear-gradient(135deg,#1d4ed8,#2563eb); cursor:pointer; box-shadow:0 4px 14px rgba(37,99,235,.3); transition:transform .15s, box-shadow .18s; position:relative; overflow:hidden; }
            .afm-btn-apply::after { content:''; position:absolute; inset:0; background:linear-gradient(135deg,rgba(255,255,255,.1),transparent 60%); }
            .afm-btn-apply:hover { transform:translateY(-1px); box-shadow:0 8px 24px rgba(37,99,235,.4); }

            @media (max-width:540px) { .afm-panel { max-width:100%; } .afm-header, .afm-body, .afm-footer { padding-left:18px; padding-right:18px; } }
        `}</style>
        </>
    )

}


export default ScheduleFilterModal;